import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface MonthDateNavigationProps {
  currentDate: DateTime;
  onDateChange: (date: DateTime) => void;
  onPrevious: () => void;
  onNext: () => void;
  className?: string;
}

export function MonthDateNavigation({
  currentDate,
  onDateChange,
  onPrevious,
  onNext,
  className,
}: MonthDateNavigationProps) {
  // Calculate month start and end
  const monthStart = currentDate.startOf("month");
  const _monthEnd = currentDate.endOf("month");

  // Quick month navigation options
  const currentYear = DateTime.now().year;
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = DateTime.fromObject({ year: currentYear, month: i + 1 });
    return {
      label: date.toFormat("MMMM"),
      date: date.startOf("month"),
    };
  });

  return (
    <div className={cn("flex items-center gap-3", className)}>
      {/* Previous Month Button */}
      <Button variant="ghost" size="icon" onClick={onPrevious} className="h-8 w-8">
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {/* Month/Year Selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 h-8 px-3">
            <CalendarDays className="h-3.5 w-3.5" />
            <span className="text-sm font-medium">{currentDate.toFormat("MMMM yyyy")}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="max-h-64 overflow-y-auto">
          <DropdownMenuLabel>Select Month</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {monthOptions.map((option) => (
            <DropdownMenuItem
              key={option.label}
              onClick={() => onDateChange(option.date)}
              className={cn(
                "cursor-pointer",
                monthStart.hasSame(option.date, "month") && "bg-accent"
              )}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Next Month Button */}
      <Button variant="ghost" size="icon" onClick={onNext} className="h-8 w-8">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
